import type { LocationQuery } from 'vue-router';

type DeepLinkRouteResult = {
  path: string;
  query: Record<string, string>;
};

/**
 * DeepLink / AppLinkのクエリパラメータをNuxtのルートに変換する
 *
 * @description
 * staticページ側で行っていたURL変換処理をNuxt側で行う。
 * 以下のパターンに一致する場合は変換後のルートを返し、
 * 一致しない場合は null を返す。
 *
 * @example
 * // redirect_path
 * ?redirect_path=path%3Fquery%3Dvalue&status=success → { path: '/path', query: { query: 'value', status: 'success' } }
 *
 * // parking
 * ?parking=123&garageId=456&step=3 → { path: '/parking/123', query: { garageId: '456', step: '3' } }
 *
 * // coupon
 * ?coupon=123 → { path: '/coupon/123', query: {} }
 *
 * // sign-in
 * ?sign-in= → { path: '/sign-in', query: {} }
 */
export const resolveDeepLinkPath = (
  query: LocationQuery,
): DeepLinkRouteResult | null => {
  const getString = (value: LocationQuery[string]): string | null => {
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
  };

  const toStringRecord = (
    q: LocationQuery,
    excludeKeys: string[] = [],
  ): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(q)) {
      if (excludeKeys.includes(key)) continue;
      const v = getString(value);
      if (v !== null) result[key] = v;
    }
    return result;
  };

  if (query.redirect_path !== undefined) {
    const redirectPath = getString(query.redirect_path);
    if (!redirectPath) return null;

    const dummyUrl = new URL(
      `https://example.com/${decodeURIComponent(decodeURIComponent(redirectPath))}`,
    );
    const dummyParams = new URLSearchParams(dummyUrl.search);

    // redirect_path以外のクエリパラメータを追加する
    for (const [key, value] of Object.entries(query)) {
      if (key === 'redirect_path') continue;
      const v = getString(value);
      if (v !== null) dummyParams.append(key, v);
    }

    const pathName = dummyUrl.pathname.replace(/^\/+/, '');
    const mergedQuery: Record<string, string> = {};
    dummyParams.forEach((value, key) => {
      mergedQuery[key] = value;
    });

    return { path: `/${pathName}`, query: mergedQuery };
  }

  if (query.parking !== undefined) {
    const parkingId = getString(query.parking);
    if (!parkingId) return null;

    return {
      path: `/parking/${parkingId}`,
      query: toStringRecord(query, ['parking']),
    };
  }

  if (query.coupon !== undefined) {
    const couponId = getString(query.coupon);
    if (!couponId) return null;

    return { path: `/coupon/${couponId}`, query: {} };
  }

  if ('sign-in' in query) {
    return { path: '/sign-in', query: {} };
  }

  return null;
};
